import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Link } from "wouter";
import { useTranslation } from 'react-i18next';
import {
  CreditCard,
  Settings,
  BarChart3,
  Shield,
  Star,
  Crown,
  User,
  Calendar
} from "lucide-react";

export default function Home() {
  const { user } = useAuth();
  const { t } = useTranslation();

  // Fetch user's subscription
  const { data: subscription } = useQuery({
    queryKey: ['/api/subscription'],
    retry: false,
  });

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <Crown className="w-5 h-5 text-red-500" />;
      case 'premium':
        return <Star className="w-5 h-5 text-yellow-500" />;
      default:
        return <User className="w-5 h-5 text-blue-500" />;
    }
  };

  const getRoleBadge = (role: string) => {
    const variants = {
      admin: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      premium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      user: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      guest: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    };
    
    return (
      <Badge className={variants[role as keyof typeof variants] || variants.guest}>
        {role.charAt(0).toUpperCase() + role.slice(1)}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <Avatar className="h-16 w-16">
              <AvatarImage src={user?.profileImageUrl || undefined} />
              <AvatarFallback className="text-lg">
                {user?.firstName?.[0] || user?.email?.[0]?.toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
                {t('dashboard.welcomeBack')}{user?.firstName ? `, ${user.firstName}` : ''}!
              </h1>
              <div className="flex items-center space-x-2 mt-2">
                {getRoleIcon(user?.role || 'user')}
                {getRoleBadge(user?.role || 'user')}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <User className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-300">{t('dashboard.accountStatus')}</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {user?.isActive ? t('dashboard.active') : t('dashboard.inactive')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                  <Shield className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-300">{t('dashboard.emailStatus')}</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {user?.isEmailVerified ? t('dashboard.verified') : t('dashboard.pending')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                  <CreditCard className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-300">{t('dashboard.subscription')}</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {subscription && (subscription as any).planType ? String((subscription as any).planType) : t('dashboard.free')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                  <Calendar className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-300">{t('dashboard.memberSince')}</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {user?.createdAt ? new Date(user.createdAt).getFullYear() : 'N/A'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Quick Actions */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Settings className="w-5 h-5 mr-2" />
                  {t('dashboard.quickActions')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Button asChild className="h-20 flex-col">
                    <Link href="/profile">
                      <User className="w-6 h-6 mb-2" />
                      Edit Profile
                    </Link>
                  </Button>
                  
                  {/* Dashboard button temporarily hidden */}
                  {/* <Button asChild variant="outline" className="h-20 flex-col">
                    <Link href="/dashboard">
                      <BarChart3 className="w-6 h-6 mb-2" />
                      View Dashboard
                    </Link>
                  </Button> */}

                  {user?.role !== 'premium' && (
                    <Button asChild variant="outline" className="h-20 flex-col">
                      <Link href="/subscribe">
                        <Star className="w-6 h-6 mb-2" />
                        Upgrade to Premium
                      </Link>
                    </Button>
                  )}

                  {user?.role === 'admin' && (
                    <Button asChild variant="outline" className="h-20 flex-col">
                      <Link href="/admin">
                        <Crown className="w-6 h-6 mb-2" />
                        Admin Panel
                      </Link>
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Subscription Info */}
            {subscription && (
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <CreditCard className="w-5 h-5 mr-2" />
                    {t('dashboard.subscription')} Details
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600 dark:text-slate-300">Plan:</span>
                      <Badge>{(subscription as any).planType || 'Free'}</Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600 dark:text-slate-300">Status:</span>
                      <Badge variant={(subscription as any).status === 'active' ? 'default' : 'secondary'}>
                        {(subscription as any).status || 'Unknown'}
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600 dark:text-slate-300">Billing:</span>
                      <span className="font-medium">{(subscription as any).billingInterval || 'monthly'}ly</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600 dark:text-slate-300">Next billing:</span>
                      <span className="font-medium">
                        {(subscription as any).currentPeriodEnd 
                          ? new Date((subscription as any).currentPeriodEnd).toLocaleDateString()
                          : 'N/A'
                        }
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Account Overview */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle>{t('dashboard.accountOverview')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Email</label>
                    <p className="text-slate-900 dark:text-white">{user?.email || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Name</label>
                    <p className="text-slate-900 dark:text-white">
                      {user?.firstName && user?.lastName 
                        ? `${user.firstName} ${user.lastName}` 
                        : 'Not provided'
                      }
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Role</label>
                    <div className="mt-1">
                      {getRoleBadge(user?.role || 'user')}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Last Login</label>
                    <p className="text-slate-900 dark:text-white">
                      {user?.lastLoginAt 
                        ? new Date(user.lastLoginAt).toLocaleDateString()
                        : 'Never'
                      }
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Upgrade Card */}
            {user?.role !== 'premium' && (
              <Card className="mt-6 border-primary bg-gradient-to-br from-primary/5 to-secondary/5">
                <CardHeader>
                  <CardTitle className="flex items-center text-primary">
                    <Star className="w-5 h-5 mr-2" />
                    {t('dashboard.upgradeToPremium')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
                    Unlock advanced features, priority support, and exclusive content.
                  </p>
                  <Button asChild className="w-full">
                    <Link href="/subscribe">
                      Start Free Trial
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
